import { observer } from "mobx-react";
import React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid, Typography, CircularProgress, Alert, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { ExtractedObject } from "../../api/clients.api";
import { useInstance } from "react-ioc";
import { ApiClients } from "../../services/AxiosBaseClient";
import { IntegrationSettingOrderParserType } from "../../types/integration";

export interface DialogAIExtractionProps {
    closeModal: () => void;
    emailId: number;
    integrationId: number;
}

export const DialogAIExtraction = observer((props: DialogAIExtractionProps) => {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [extractedData, setExtractedData] = React.useState<ExtractedObject | null>(null);
    const [extractionType, setExtractionType] = React.useState<IntegrationSettingOrderParserType>(IntegrationSettingOrderParserType.AI);
    const companyApiClients = useInstance(ApiClients);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await companyApiClients.emailInboxClient.extractParamsFromEmail(
                props.integrationId,
                props.emailId,
                extractionType
            );
            setExtractedData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to extract data from email");
        } finally {
            setLoading(false);
        }
    };

    const renderField = (label: string, value: any) => {
        if (value === undefined || value === null) return null;
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    label={label}
                    value={value}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                />
            </Grid>
        );
    };

    return (
        <Dialog
            open={true}
            onClose={props.closeModal}
            aria-labelledby="ai-extraction-dialog-title"
            aria-describedby="ai-extraction-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="ai-extraction-dialog-title">
                AI Extraction Results
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="extraction-type-label">Extraction Type</InputLabel>
                            <Select
                                labelId="extraction-type-label"
                                value={extractionType}
                                label="Extraction Type"
                                onChange={(e) => setExtractionType(e.target.value as IntegrationSettingOrderParserType)}
                            >
                                {Object.entries(IntegrationSettingOrderParserType)
                                    .filter(([key]) => isNaN(Number(key)))
                                    .map(([key, value]) => (
                                        <MenuItem value={value as number} key={key}>{key}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={fetchData}
                            disabled={loading}
                            fullWidth
                        >
                            Extract Data
                        </Button>
                    </Grid>
                </Grid>

                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <CircularProgress />
                    </div>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && extractedData && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>Pickup Information</Typography>
                        </Grid>
                        {renderField("Pickup City", extractedData.pickUpCity)}
                        {renderField("Pickup State", extractedData.pickUpState)}
                        {renderField("Pickup ZIP", extractedData.pickUpZip)}
                        {renderField("Pickup ASAP", extractedData.pickUpASAP ? "Yes" : "No")}
                        {renderField("Pickup Date/Time", extractedData.pickUpDateTime?.format("MM/DD/YYYY HH:mm"))}

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Delivery Information</Typography>
                        </Grid>
                        {renderField("Delivery City", extractedData.deliveryCity)}
                        {renderField("Delivery State", extractedData.deliveryState)}
                        {renderField("Delivery ZIP", extractedData.deliveryZip)}
                        {renderField("Deliver Direct", extractedData.deliverDirect ? "Yes" : "No")}
                        {renderField("Delivery Date/Time", extractedData.deliveryDateTime?.format("MM/DD/YYYY HH:mm"))}

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Load Details</Typography>
                        </Grid>
                        {renderField("Miles", extractedData.miles)}
                        {renderField("Vehicle Type", extractedData.vehicleType)}
                        {renderField("Load Type", extractedData.loadType)}
                        {extractedData.pallets && extractedData.pallets.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>Pallets</Typography>
                                {extractedData.pallets.map((pallet, index) => (
                                    <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
                                        {renderField("Tag", pallet.tag)}
                                        {renderField("Length", pallet.length)}
                                        {renderField("Width", pallet.width)}
                                        {renderField("Height", pallet.height)}
                                        {renderField("Weight", pallet.weight)}
                                        {renderField("Can Stack", pallet.canStack ? "Yes" : "No")}
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {renderField("Pays", extractedData.pays)}

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Additional Information</Typography>
                        </Grid>
                        {renderField("Phone", extractedData.phone)}
                        {renderField("Posted By", extractedData.postedBy)}
                        {renderField("Posted By Email", extractedData.postedByEmail)}
                        {renderField("Order References", extractedData.orderReferences)}
                        {renderField("Notes", extractedData.notes)}
                        {renderField("Hazardous", extractedData.hazardous ? "Yes" : "No")}
                        {renderField("Fast Load", extractedData.fastLoad ? "Yes" : "No")}
                        {renderField("Dock Level", extractedData.dockLevel ? "Yes" : "No")}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.closeModal}>Close</Button>
            </DialogActions>
        </Dialog>
    );
});
